// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { StringUtils } from '@shared/utils/StringUtils';
import { Resources } from '@shared/localizations/Resources';
import { AgressorFlagType } from '@shared/utils/Enums/Constants';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
export class TimeSalesItem extends BaseItem {
    constructor() {
        super(...arguments);
        this.instrument = null;
        this.isSeparatedSourceMode = null;
        this.price = null;
        this.time = null;
        this.size = null;
        this.exchange = null;
        this.buyerSource = null;
        this.sellerSource = null;
        this.aggressorFlag = null;
        this.colorValue = null;
        this.itemType = null;
        this.lastVenue = null;
    }
    ColumnCount() {
        return TimeSalesItem.columnsParams.length;
    }
    getColumnData(column) {
        const val = this.getColumnValue(column);
        let fVal = null;
        const ins = this.instrument;
        switch (column) {
            case 1:
                fVal = DateTimeUtils.formatDate(val, 'HH:mm:ss');
                break;
            case 2:
                fVal = ins.formatPrice(val);
                break;
            case 3:
                fVal = ins.DataCache.formatVolume(ins, val, WDSettingsUtils.displayAmountInLots());
                break;
            case 11: {
                const agrFlagTypes = AgressorFlagType;
                let locKey = null;
                if (val == -2) {
                    locKey = '';
                }
                else if (val === agrFlagTypes.None || val === agrFlagTypes.NotSet) {
                    locKey = 'panel.timeSales.AgressorFlag.None';
                }
                else if (val === agrFlagTypes.Ask) {
                    locKey = 'panel.timeSales.AgressorFlag.Buy';
                }
                else if (val === agrFlagTypes.Bid) {
                    locKey = 'panel.timeSales.AgressorFlag.Sell';
                }
                else if (val === agrFlagTypes.CrossTrade) {
                    locKey = 'panel.timeSales.AgressorFlag.CrossTrade';
                }
                else if (val === agrFlagTypes.RLP) {
                    locKey = 'panel.timeSales.AgressorFlag.RLP';
                }
                else {
                    locKey = 'panel.timeSales.AgressorFlag.Auction';
                }
                fVal = Resources.getResource(locKey);
                break;
            }
            case 12:
            case 13:
                fVal = val === null || val === undefined ? StringUtils.NOT_AVAILABLE : val.toString();
                break;
            default:
                fVal = val === null || val === undefined ? '' : val.toString();
        }
        return new ColumnData(val, fVal);
    }
    getColumnValue(column) {
        const ins = this.instrument;
        const dc = ins.DataCache;
        switch (column) {
            case 0:
                return ins.DisplayName();
            case 1:
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(this.time);
            case 2:
                return this.price;
            case 3:
                return this.size;
            case 4:
                return this.lastVenue != null ? dc.GetSourceName(this.lastVenue) : this.exchange;
            case 9:
                return ''; // брать BID_VENUE из Quote (1) когда TimeSalesPanel начнет поддерживать Q1
            case 10:
                return ''; // брать ASK_VENUE из Quote (1) когда TimeSalesPanel начнет поддерживать Q1
            case 11:
                return this.aggressorFlag;
            case 12:
                return this.buyerSource;
            case 13:
                return this.sellerSource;
            default:
                return '';
        }
    }
    // TODO. Ugly. Static modification in each item.
    GetColumnParams(column) {
        const result = TimeSalesItem.columnsParams[column];
        let isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        if (column === 4) {
            isHidden = isHidden || this.isSeparatedSourceMode;
        }
        if (column === 12 || column === 13) {
            isHidden = isHidden || !this.isSeparatedSourceMode;
        }
        result.Hidden = isHidden || result.Hidden;
        return result;
    }
}
TimeSalesItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.timeSales.Instrument', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    /* 1 */ new ColumnParams('panel.timeSales.Time', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    /* 2 */ new ColumnParams('panel.timeSales.Price', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 3 */ new ColumnParams('panel.timeSales.Size', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 4 */ new ColumnParams('panel.timeSales.Exchange', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.timeSales.Type', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false, true),
    new ColumnParams('panel.timeSales.bid', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false, true),
    new ColumnParams('panel.timeSales.ask', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false, true),
    new ColumnParams('panel.timeSales.AskBidSize', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false, true),
    new ColumnParams('panel.timeSales.BidSource', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.timeSales.AskSource', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    /* 11 */ new ColumnParams('panel.timeSales.Agressor flag', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 12 */ new ColumnParams('panel.timeSales.BuyerSource', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    /* 13 */ new ColumnParams('panel.timeSales.SellerSource', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false)
];
export var TSItemType;
(function (TSItemType) {
    TSItemType[TSItemType["AboveAsk"] = 0] = "AboveAsk";
    TSItemType[TSItemType["AtAsk"] = 1] = "AtAsk";
    TSItemType[TSItemType["AtBid"] = 2] = "AtBid";
    TSItemType[TSItemType["BelowBid"] = 3] = "BelowBid";
    TSItemType[TSItemType["BetweenSpread"] = 4] = "BetweenSpread";
    TSItemType[TSItemType["Quotes"] = 5] = "Quotes"; // cQUOTE
})(TSItemType || (TSItemType = {}));
